'use client';

import React, { Suspense, lazy, Component, ReactNode } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import HeroSection from './components/HeroSection';

// Lazy load below-fold sections for performance
const FeatureCategoriesSection = lazy(() => import('./components/FeatureCategoriesSection'));
const TrendingDestinationsSection = lazy(() => import('./components/TrendingDestinationsSection'));
const TopHotelsRestaurantsSection = lazy(() => import('./components/TopHotelsRestaurantsSection'));
const TreksAdventureSection = lazy(() => import('./components/TreksAdventureSection'));
const HiddenGemsCommunitySection = lazy(() => import('./components/HiddenGemsCommunitySection'));
const GuidesFlightsDealsSection = lazy(() => import('./components/GuidesFlightsDealsSection'));
const FestivalsSection = lazy(() => import('./components/FestivalsSection'));
const LiveStatsAISection = lazy(() => import('./components/LiveStatsAISection'));
const TrendingInfoSection = lazy(() => import('./components/TrendingInfoSection'));
const NewsletterSection = lazy(() => import('./components/NewsletterSection'));

interface ChunkErrorBoundaryState {
  hasError: boolean;
  retried: boolean;
}

class ChunkErrorBoundary extends Component<{ children: ReactNode; fallback?: ReactNode }, ChunkErrorBoundaryState> {
  constructor(props: { children: ReactNode; fallback?: ReactNode }) {
    super(props);
    this.state = { hasError: false, retried: false };
  }

  static getDerivedStateFromError(): ChunkErrorBoundaryState {
    return { hasError: true, retried: false };
  }

  componentDidCatch(error: Error) {
    // Auto-reload once on ChunkLoadError (stale cache after new deploy)
    if (
      error.name === 'ChunkLoadError' || error.message?.includes('Loading chunk') ||
      error.message?.includes('Failed to fetch dynamically imported module')
    ) {
      if (!this.state.retried && typeof window !== 'undefined') {
        this.setState({ retried: true });
        window.location.reload();
      }
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? null;
    }
    return this.props.children;
  }
}

function SectionSkeleton() {
  return (
    <div className="py-16 px-4 max-w-7xl mx-auto">
      <div className="h-6 w-40 mb-2 mx-auto rounded-lg animate-pulse" style={{ background: 'rgba(245,240,232,0.06)' }} />
      <div className="h-4 w-56 mb-8 mx-auto rounded-lg animate-pulse" style={{ background: 'rgba(245,240,232,0.04)' }} />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {[1, 2, 3]?.map(i => (
          <div key={i} className="h-56 rounded-2xl animate-pulse" style={{ background: 'rgba(245,240,232,0.04)' }} />
        ))}
      </div>
    </div>
  );
}

export default function HomePage() {
  return (
    <div className="min-h-screen" style={{ background: 'var(--royal-blue)' }}>
      <Header />
      <HeroSection />

      <ChunkErrorBoundary fallback={<SectionSkeleton />}>
        <Suspense fallback={<SectionSkeleton />}>
          <FeatureCategoriesSection />
        </Suspense>
      </ChunkErrorBoundary>

      <ChunkErrorBoundary fallback={<SectionSkeleton />}>
        <Suspense fallback={<SectionSkeleton />}>
          <TrendingDestinationsSection />
        </Suspense>
      </ChunkErrorBoundary>

      <ChunkErrorBoundary fallback={<SectionSkeleton />}>
        <Suspense fallback={<SectionSkeleton />}>
          <TopHotelsRestaurantsSection />
        </Suspense>
      </ChunkErrorBoundary>

      <ChunkErrorBoundary fallback={<SectionSkeleton />}>
        <Suspense fallback={<SectionSkeleton />}>
          <TreksAdventureSection />
        </Suspense>
      </ChunkErrorBoundary>

      <ChunkErrorBoundary fallback={<SectionSkeleton />}>
        <Suspense fallback={<SectionSkeleton />}>
          <HiddenGemsCommunitySection />
        </Suspense>
      </ChunkErrorBoundary>

      <ChunkErrorBoundary fallback={<SectionSkeleton />}>
        <Suspense fallback={<SectionSkeleton />}>
          <GuidesFlightsDealsSection />
        </Suspense>
      </ChunkErrorBoundary>

      <ChunkErrorBoundary fallback={<SectionSkeleton />}>
        <Suspense fallback={<SectionSkeleton />}>
          <FestivalsSection />
        </Suspense>
      </ChunkErrorBoundary>

      <ChunkErrorBoundary fallback={<SectionSkeleton />}>
        <Suspense fallback={<SectionSkeleton />}>
          <LiveStatsAISection />
        </Suspense>
      </ChunkErrorBoundary>

      <ChunkErrorBoundary fallback={<SectionSkeleton />}>
        <Suspense fallback={<SectionSkeleton />}>
          <TrendingInfoSection />
        </Suspense>
      </ChunkErrorBoundary>

      <ChunkErrorBoundary fallback={<SectionSkeleton />}>
        <Suspense fallback={<SectionSkeleton />}>
          <NewsletterSection />
        </Suspense>
      </ChunkErrorBoundary>

      <Footer />
    </div>
  );
}